import React, { useEffect, useRef } from "react"
import { INSTITUTES, getIcon } from "../constants"
import { Link } from "react-router-dom"
import {
  ArrowRight,
  Building2,
  Fingerprint,
  ShieldCheck,
  Activity,
  Plus,
  Box,
  Binary,
  Microscope,
  Award,
  Zap,
  Phone,
  MapPin
} from "lucide-react"

const Institutes = () => {
  const observerRef = useRef(null)

  useEffect(() => {
    document.title =
      "Specialized Institutes | VCNR Hospital Clinical Excellence"
    window.scrollTo(0, 0)

    observerRef.current = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active")
          }
        })
      },
      { threshold: 0.1 }
    )

    document.querySelectorAll(".reveal").forEach(el => {
      observerRef.current?.observe(el)
    })

    return () => observerRef.current?.disconnect()
  }, [])

  return (
    <div className="bg-[#fcfcfd] min-h-screen">
      {/* --- HERO SECTION --- */}
      <section className="relative pt-48 pb-32 overflow-hidden bg-white">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[60%] h-[100%] bg-gradient-to-l from-vcnr-brand/5 to-transparent"></div>
          <div className="absolute top-[20%] left-[10%] w-72 h-72 bg-vcnr-brand/10 blur-[120px] rounded-full"></div>
          <div className="absolute inset-0 hospital-grid opacity-[0.03]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-12">
            <div className="max-w-3xl">
              {/* <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 border border-slate-200 mb-8 animate-fade-in-up">
                <span className="w-2 h-2 rounded-full bg-vcnr-brand animate-pulse"></span>
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
                  Tier-1 Clinical Governance
                </span>
              </div> */}
              <h1 className="text-5xl md:text-6xl font-semibold   mb-6">
                Medical <br />
                <span className="text-vcnr-brand ">
                  Ecosystem.
                </span>
              </h1>
              <p className="text-slate-600 text-lg mb-8">
                VCNR’s specialized institutes are engineered for outcomes. We
                integrate nine distinct centers of excellence into a single,
                unified therapeutic hub.
              </p>
            </div>

            <div className="flex flex-col gap-6 reveal stagger-2">
              <div className="p-8 bg-white border border-slate-100 shadow-xl shadow-slate-200/40 rounded-[2rem] flex items-center gap-6 group hover:scale-105 transition-transform">
                <div className="w-14 h-14 bg-vcnr-brand/10 text-vcnr-brand rounded-2xl flex items-center justify-center group-hover:bg-vcnr-brand group-hover:text-white transition-colors">
                  <Building2
                    size={24}
                    className="group-hover:rotate-12 transition-transform"
                  />
                </div>
                <div>
                  <div className="text-2xl font-black text-slate-900">09</div>
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    Global Institutes
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- UNIFORM BLUEPRINT DIRECTORY --- */}
      <section className="pb-32 px-6">
        <div className="max-w-7xl mx-auto space-y-20">
          {INSTITUTES.map((inst, index) => (
            <div
              key={inst.id}
              className={`group relative bg-white rounded-[3.5rem] overflow-hidden border border-slate-100 reveal transition-all duration-700 hover:shadow-[0_80px_120px_-30px_rgba(79,70,229,0.15)] flex flex-col ${
                index % 2 === 0 ? "stagger-1" : "stagger-2"
              }`}
            >
              <div className="flex flex-col lg:flex-row items-stretch">
                {/* 1. Technical Sidebar (Fixed width, height stretches with content) */}
                <div
                  className={`hidden lg:flex w-24 bg-slate-50 border-slate-100 flex-col items-center py-16 justify-between shrink-0 transition-colors duration-500 group-hover:bg-slate-100 ${
                    index % 2 === 0
                      ? "border-r order-first"
                      : "border-l order-last"
                  }`}
                >
                  <div className="text-[9px] font-black text-slate-400 uppercase [writing-mode:vertical-lr] rotate-180 tracking-[0.6em] opacity-40 group-hover:opacity-100 group-hover:text-vcnr-brand transition-all">
                    VCNR_HUB_UNIT_{String(index + 1).padStart(2, "0")}
                  </div>
                  <div className="w-12 h-12 rounded-2xl bg-white border border-slate-100 flex items-center justify-center text-vcnr-brand shadow-sm transition-all group-hover:bg-vcnr-brand group-hover:text-white group-hover:rotate-[360deg] duration-1000">
                    <Binary size={20} />
                  </div>
                </div>

                {/* 2. Main Content Wrapper */}
                <div className="flex flex-col grow">
                  {/* Internal Grid: Image vs Text */}
                  <div className="grid lg:grid-cols-2">
                    {/* Visual Module: Set height min to prevent overlapping and allow grid to define actual height */}
                    <div
                      className={`relative h-[400px] lg:h-auto min-h-[450px] overflow-hidden ${
                        index % 2 !== 0 ? "lg:order-last" : "lg:order-first"
                      }`}
                    >
                      {/* Scanning Line Animation */}
                      <div className="scanning-line"></div>

                      <img
                        src={inst.image}
                        alt={inst.name}
                        className="absolute inset-0 w-full h-full object-contain grayscale brightness-[0.85] group-hover:grayscale-0 group-hover:brightness-100 transition-all duration-[1.5s] group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent opacity-60 group-hover:opacity-20 transition-opacity"></div>

                      {/* Technical Overlay */}
                      {/* <div className="absolute top-10 left-10 z-30 flex flex-col gap-3">
                        <div className="bg-white/90 backdrop-blur-xl px-4 py-2 rounded-xl border border-white/50 shadow-2xl inline-flex items-center gap-2 transform transition-all group-hover:translate-x-2">
                          <ShieldCheck
                            size={14}
                            className="text-vcnr-brand animate-pulse"
                          />
                          <span className="text-[10px] font-black uppercase tracking-widest text-slate-900">
                            Governance Tier-1
                          </span>
                        </div>
                        <div className="bg-vcnr-brand/90 backdrop-blur-xl px-4 py-2 rounded-xl border border-vcnr-brand/50 shadow-2xl inline-flex items-center gap-2 text-white transform transition-all delay-75 group-hover:translate-x-2">
                          <Microscope size={14} />
                          <span className="text-[10px] font-black uppercase tracking-widest">
                            Evidence-Based
                          </span>
                        </div>
                      </div> */}

                      {/* Icon Badge */}
                      <div className="absolute bottom-10 right-10 z-30 w-24 h-24 bg-white rounded-[2rem] shadow-2xl border border-slate-100 flex items-center justify-center text-vcnr-brand transform group-hover:-translate-y-4 group-hover:rotate-6 transition-all duration-700">
                        {getIcon(inst.icon)}
                      </div>
                    </div>

                    {/* Information Module: Vertical Padding ensures no overlap if text is short */}
                    <div className="p-10 lg:p-16 xl:p-20 flex flex-col justify-center bg-white">
                      <div className="flex items-center gap-4 mb-8">
                        <div className="w-1 h-12 bg-vcnr-brand rounded-full animate-vital-pulse"></div>
                        <div>
                          <div className="text-vcnr-brand text-[12px] font-bold uppercase tracking-[0.1em] group-hover:tracking-[0.6em] transition-all">
                            Center of Excellence
                          </div>
                          <h2 className="text-4xl md:text-[3rem] font-semibold text-slate-900  leading-tight group-hover:text-vcnr-brand transition-colors">
                            {inst.name.replace("VCNR ", "")}
                          </h2>
                        </div>
                      </div>

                      <p className="text-slate-600 text-lg mb-8">
                        {inst.description}
                      </p>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mb-12">
                        {inst.services.map((service, sIdx) => (
                          <div
                            key={sIdx}
                            className="flex items-center gap-3 py-2 border-b border-slate-50 group/item overflow-hidden"
                          >
                            <Zap
                              size={12}
                              className="text-vcnr-brand group-hover/item:text-vcnr-accent -translate-x-4 opacity-0 group-hover/item:translate-x-0 group-hover/item:opacity-100 transition-all duration-300"
                            />
                            <span className="text-[12px] font-semibold text-slate-600 uppercase tracking-widest group-hover/item:text-vcnr-brand group-hover/item:translate-x-1 transition-all duration-300">
                              {service}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="flex items-center flex-wrap gap-6">
                        <Link
                          to={`/institute/${inst.id}`}
                          className="px-10 py-5 bg-slate-900 text-white rounded-2xl text-[14px] font-semibold   hover:bg-vcnr-brand transition-all shadow-xl hover:scale-105 active:scale-95 flex items-center gap-4 group/btn"
                        >
                          Explore Hub{" "}
                          <ArrowRight
                            size={16}
                            className="group-hover/btn:translate-x-2 transition-transform"
                          />
                        </Link>
                        <div className="flex items-center gap-3 px-6 py-4 rounded-2xl bg-slate-50 border border-slate-100 transition-colors group-hover:border-vcnr-brand/20">
                          <Award
                            size={18}
                            className="text-vcnr-brand animate-vital-pulse"
                          />
                          <span className="text-[14px] font-semibold uppercase  text-slate-400">
                            NABH Accredited Unit
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 3. Bottom Diagnostic Data Tray */}
                  {/* <div className="bg-slate-50/70 border-t border-slate-100 p-8 lg:p-10 grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 group-hover:bg-slate-100/50 transition-colors duration-500">
                    {[
                      {
                        label: "Clinical Precision",
                        val: `${(97 + Math.random() * 2).toFixed(1)}%`,
                        icon: <Activity size={14} />
                      },
                      {
                        label: "Staff Count",
                        val: `${Math.floor(15 + Math.random() * 10)}+ Experts`,
                        icon: <Plus size={14} />
                      },
                      {
                        label: "Critical Care",
                        val: "Tier-1 Ready",
                        icon: <ShieldCheck size={14} />
                      },
                      {
                        label: "Diagnostic Load",
                        val: "Real-time",
                        icon: <Box size={14} />
                      }
                    ].map((stat, sIdx) => (
                      <div
                        key={sIdx}
                        className="flex flex-col gap-2 group/stat"
                      >
                        <div className="flex items-center gap-3 text-slate-400 group-hover/stat:text-vcnr-brand transition-colors">
                          <span className="group-hover/stat:rotate-[360deg] transition-transform duration-1000">
                            {stat.icon}
                          </span>
                          <span className="text-[10px] font-black uppercase tracking-widest">
                            {stat.label}
                          </span>
                        </div>
                        <div className="text-lg font-black text-slate-900 tracking-tight transform transition-all group-hover/stat:scale-110 origin-left">
                          {stat.val}
                        </div>
                      </div>
                    ))}
                  </div> */}

       
                </div>

                          
              </div>

              
            </div>
          ))}
        </div>

        
      </section>

    <section className="py-20 bg-slate-50 reveal">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-vcnr-brand rounded-[2.5rem] p-12 md:p-20 text-white flex flex-col items-center text-center relative overflow-hidden shadow-2xl group">
            <div className="relative z-10">
              <h2 className="text-4xl md:text-[3rem]  font-semibold text-slate-900 mb-8 leading-tight text-white">
                Specialized Care is a <br /> Click Away.
              </h2>
              <p className="text-lg text-white/70 mb-12 max-w-2xl mx-auto">
                Our specialized medical board is available for professional
                consultation. We prioritize your recovery and well-being with
                international quality standards.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-6">
                <Link
                  to="/booking"
                  className="bg-white text-vcnr-brand px-12 py-5 rounded-xl font-black uppercase text-xs tracking-[0.1em] shadow-xl hover:bg-slate-900 hover:text-white transition-all hover:scale-110 active:scale-95"
                >
                  Request Appointment
                </Link>
                <a
                  href="tel:1066"
                  className="bg-red-600 text-white px-12 py-5 rounded-xl font-black uppercase text-xs tracking-[0.1em] flex items-center justify-center gap-3 hover:bg-red-700 transition-all hover:scale-110 active:scale-95"
                >
                  <Phone size={18} /> Emergency: 1066
                </a>
              </div>
            </div>
            <div className="absolute inset-0 opacity-10 pointer-events-none hospital-grid group-hover:scale-150 transition-transform duration-[20s]"></div>
          </div>
        </div>
      </section>

        <div className="bg-slate-900 py-10 px-6">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
                  <div className="flex items-center gap-6">
                    <div className="w-12 h-12 bg-vcnr-brand/20 rounded-xl flex items-center justify-center text-vcnr-brand">
                      <MapPin size={24} />
                    </div>
                    <div>
                      <div className="text-[12px] font-semibold text-slate-400 uppercase tracking-widest mb-1">
                        Facility Address
                      </div>
                      <div className="  text-white uppercase ">
                        Opp Binnamangala Nursery, B.H. Road, Nelamangala - 562123,
                        Bangalore.
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <a
                      href="/booking"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-8 py-3 bg-white text-slate-900 rounded-xl text-[12px] font-bold uppercase  hover:bg-vcnr-brand hover:text-white transition-all"
                    >
                      Contact Us
                    </a>
                  </div>
                </div>
              </div>
    </div>
  )
}

export default Institutes
